import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, Download, Mail, Building2, BookOpen, Users } from "lucide-react";

const TABS = [
    { key: "stats", label: "Overview" },
    { key: "waitlist", label: "Waitlist" },
    { key: "enterprises", label: "Directory" },
    { key: "blog", label: "Founder Stories" },
    { key: "contacts", label: "Contacts" },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("stats");

    useEffect(() => {
        const prev = document.title;
        document.title = "Admin Dashboard | Purposeful, Inc.";
        let robots = document.querySelector('meta[name="robots"]');
        const orig = robots ? robots.getAttribute("content") : null;
        if (!robots) {
            robots = document.createElement("meta");
            robots.setAttribute("name", "robots");
            document.head.appendChild(robots);
        }
        robots.setAttribute("content", "noindex, nofollow");
        return () => {
            document.title = prev;
            if (orig !== null) robots.setAttribute("content", orig);
        };
    }, []);

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-black text-white">
            <header className="border-b border-white/10 bg-black/60 backdrop-blur">
                <div className="container-x mx-auto max-w-7xl flex items-center justify-between h-16">
                    <div className="font-display text-lg font-bold">Purposeful<span className="text-[#D4AF37]">,</span> Admin</div>
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-zinc-400 hidden sm:block">{user?.email}</span>
                        <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-zinc-300 hover:text-[#D4AF37] text-xs font-mono uppercase tracking-widest">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="container-x mx-auto max-w-7xl py-10">
                <div className="flex flex-wrap gap-2 mb-10">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`admin-tab-${t.key}`}
                            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition ${
                                tab === t.key
                                    ? "bg-[#D4AF37] text-black"
                                    : "border border-white/15 text-zinc-300 hover:border-[#D4AF37]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "stats" && <StatsPanel />}
                {tab === "waitlist" && <WaitlistPanel />}
                {tab === "enterprises" && <EnterprisesPanel />}
                {tab === "blog" && <BlogPanel />}
                {tab === "contacts" && <ContactsPanel />}
            </div>
        </main>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="card-dark p-6">
            <Icon size={18} className="text-[#D4AF37]" />
            <div className="text-zinc-500 mt-3 text-xs font-mono uppercase tracking-widest">{label}</div>
            <div className="font-display text-4xl mt-1">{value}</div>
        </div>
    );
}

function StatsPanel() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);
    if (!stats) return <p className="text-zinc-500">Loading…</p>;
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats-grid">
            <StatCard icon={Mail} label="Waitlist" value={stats.waitlist} />
            <StatCard icon={Building2} label="Enterprises" value={stats.enterprises} />
            <StatCard icon={BookOpen} label="Blog Posts" value={stats.blog_posts} />
            <StatCard icon={Users} label="Contacts" value={stats.contacts} />
        </div>
    );
}

function WaitlistPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/admin/waitlist")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, []);

    const downloadCsv = async () => {
        const token = localStorage.getItem("purposeful_token");
        const res = await fetch(`${API}/admin/waitlist/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "waitlist.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-waitlist-panel">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl">Waitlist ({items.length})</h2>
                <button onClick={downloadCsv} data-testid="admin-waitlist-export" className="btn-ghost-gold text-xs">
                    <Download size={14} /> Export CSV
                </button>
            </div>
            {loading ? <p className="text-zinc-500">Loading…</p> : (
                <div className="card-dark overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-widest font-mono">
                            <tr>
                                <th className="text-left p-4">Email</th>
                                <th className="text-left p-4">Name</th>
                                <th className="text-left p-4">Source</th>
                                <th className="text-left p-4">Joined</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((w) => (
                                <tr key={w.id} className="border-t border-white/5" data-testid="waitlist-row">
                                    <td className="p-4">{w.email}</td>
                                    <td className="p-4 text-zinc-400">{w.name || "-"}</td>
                                    <td className="p-4 text-zinc-500 font-mono text-xs">{w.source}</td>
                                    <td className="p-4 text-zinc-500 text-xs">{new Date(w.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function EnterprisesPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const empty = { startup_name: "", founder_name: "", sector: "", description: "", website: "", founder_linkedin: "", logo_url: "", location: "", impact_statement: "" };
    const [form, setForm] = useState(empty);

    const reload = () => api.get("/enterprises").then((r) => setItems(r.data));
    useEffect(() => { reload(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing) await api.put(`/admin/enterprises/${editing}`, form);
            else await api.post("/admin/enterprises", form);
            toast.success(editing ? "Updated." : "Added.");
            setForm(empty); setEditing(null); reload();
        } catch (err) { toast.error("Save failed."); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this enterprise?")) return;
        await api.delete(`/admin/enterprises/${id}`);
        toast.success("Deleted."); reload();
    };

    const edit = (e) => { setEditing(e.id); setForm({ ...empty, ...e }); window.scrollTo({ top: 0, behavior: "smooth" }); };

    return (
        <div data-testid="admin-enterprises-panel" className="grid lg:grid-cols-2 gap-10">
            <form onSubmit={save} data-testid="admin-enterprise-form" className="card-dark p-8 space-y-3">
                <h3 className="font-display text-xl">{editing ? "Edit enterprise" : "Add enterprise"}</h3>
                {[
                    ["startup_name", "Startup name *", true],
                    ["founder_name", "Founder name *", true],
                    ["sector", "Sector *", true],
                    ["location", "Location", false],
                    ["website", "Website URL", false],
                    ["founder_linkedin", "Founder LinkedIn URL", false],
                    ["logo_url", "Logo image URL", false],
                    ["impact_statement", "Short impact statement", false],
                ].map(([key, label, required]) => (
                    <input
                        key={key}
                        required={required}
                        value={form[key] || ""}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                        placeholder={label}
                        data-testid={`enterprise-input-${key}`}
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37] transition"
                    />
                ))}
                <textarea
                    required rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Description *"
                    data-testid="enterprise-input-description"
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37] transition resize-none"
                />
                <div className="flex gap-3">
                    <button type="submit" data-testid="enterprise-save-button" className="btn-gold"><Plus size={14} /> {editing ? "Update" : "Add"}</button>
                    {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="btn-ghost-gold">Cancel</button>}
                </div>
            </form>

            <div className="space-y-3" data-testid="admin-enterprises-list">
                <h3 className="font-display text-xl">All enterprises ({items.length})</h3>
                {items.length === 0 && <p className="text-zinc-500 text-sm">No enterprises yet. Add the first.</p>}
                {items.map((e) => (
                    <div key={e.id} className="card-dark p-5 flex items-start justify-between gap-4">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">{e.sector}</div>
                            <div className="font-display text-lg mt-1">{e.startup_name}</div>
                            <div className="text-zinc-500 text-xs">By {e.founder_name}</div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => edit(e)} className="p-2 rounded-lg border border-white/10 hover:border-[#D4AF37]" data-testid={`enterprise-edit-${e.id}`}><Edit3 size={14} /></button>
                            <button onClick={() => remove(e.id)} className="p-2 rounded-lg border border-white/10 hover:border-red-500 hover:text-red-400" data-testid={`enterprise-delete-${e.id}`}><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function BlogPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const empty = { title: "", excerpt: "", substack_url: "", founder_name: "", cover_image_url: "", published_at: "" };
    const [form, setForm] = useState(empty);
    const reload = () => api.get("/blog-posts").then((r) => setItems(r.data));
    useEffect(() => { reload(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing) await api.put(`/admin/blog-posts/${editing}`, form);
            else await api.post("/admin/blog-posts", form);
            toast.success(editing ? "Updated." : "Added.");
            setForm(empty); setEditing(null); reload();
        } catch { toast.error("Save failed."); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this post?")) return;
        await api.delete(`/admin/blog-posts/${id}`);
        toast.success("Deleted."); reload();
    };
    const edit = (p) => { setEditing(p.id); setForm({ ...empty, ...p }); window.scrollTo({ top: 0, behavior: "smooth" }); };

    return (
        <div data-testid="admin-blog-panel" className="grid lg:grid-cols-2 gap-10">
            <form onSubmit={save} data-testid="admin-blog-form" className="card-dark p-8 space-y-3">
                <h3 className="font-display text-xl">{editing ? "Edit post" : "Add Substack post"}</h3>
                {[
                    ["title", "Post title *", true],
                    ["substack_url", "Substack URL *", true],
                    ["founder_name", "Founder featured", false],
                    ["cover_image_url", "Cover image URL", false],
                    ["published_at", "Published date (e.g. 2026-03-12)", false],
                ].map(([key, label, required]) => (
                    <input
                        key={key}
                        required={required}
                        value={form[key] || ""}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                        placeholder={label}
                        data-testid={`blog-input-${key}`}
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37] transition"
                    />
                ))}
                <textarea
                    required rows={4}
                    value={form.excerpt}
                    onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                    placeholder="Excerpt (will show on the card) *"
                    data-testid="blog-input-excerpt"
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37] transition resize-none"
                />
                <div className="flex gap-3">
                    <button type="submit" data-testid="blog-save-button" className="btn-gold"><Plus size={14} /> {editing ? "Update" : "Add"}</button>
                    {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="btn-ghost-gold">Cancel</button>}
                </div>
            </form>

            <div className="space-y-3" data-testid="admin-blog-list">
                <h3 className="font-display text-xl">All posts ({items.length})</h3>
                {items.length === 0 && <p className="text-zinc-500 text-sm">No posts yet. Paste your first Substack link.</p>}
                {items.map((p) => (
                    <div key={p.id} className="card-dark p-5 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] truncate">{p.founder_name || "Essay"}</div>
                            <div className="font-display text-lg mt-1">{p.title}</div>
                            <a href={p.substack_url} target="_blank" rel="noreferrer" className="text-zinc-500 text-xs truncate block max-w-md hover:text-[#D4AF37]">{p.substack_url}</a>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => edit(p)} className="p-2 rounded-lg border border-white/10 hover:border-[#D4AF37]" data-testid={`blog-edit-${p.id}`}><Edit3 size={14} /></button>
                            <button onClick={() => remove(p.id)} className="p-2 rounded-lg border border-white/10 hover:border-red-500 hover:text-red-400" data-testid={`blog-delete-${p.id}`}><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ContactsPanel() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/contacts").then((r) => setItems(r.data)); }, []);
    return (
        <div data-testid="admin-contacts-panel">
            <h2 className="font-display text-2xl mb-6">Contact messages ({items.length})</h2>
            {items.length === 0 ? (
                <p className="text-zinc-500 text-sm">No messages yet.</p>
            ) : (
                <div className="space-y-3">
                    {items.map((c) => (
                        <div key={c.id} className="card-dark p-6" data-testid="contact-row">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="font-display text-lg">{c.name} <span className="text-zinc-500 text-sm">· {c.email}</span></div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{new Date(c.created_at).toLocaleString()}</div>
                            </div>
                            {c.topic && <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mt-2">{c.topic}</div>}
                            <p className="text-zinc-300 text-sm mt-3 leading-relaxed">{c.message}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
